<template>
    <Form @submit="onSubmit" :validation-schema="schema">
        <div class="container">
            <div class="row details-wrapper mt-md-5 mt-4">
                <div class="col-xl-12">
                    <div class="has-shadow">
                        <div class="bg-white p-md-5 px-4 pt-5 pb-4">
                            <div id="error-box">
                                <div v-if="this.error" class="mb-3">
                                    <info-box v-bind:type="'error'"
                                              class="info-box info-box--error">
                                        <div class="row g-0">
                                            <div class="col-24">
                                                <div class="inner-content">
                                                    <span class="heading"> Error </span>
                                                    <div v-if="this.errorText.length">
                                                        <p>
                                                            {{ this.errorText }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </info-box>
                                </div>
                            </div>

                            <div>
                                <div v-if="!isAuthenticated">
                                    <h4 class="mb-4 details-heading">Lead booker details</h4>

                                    <div class="row">
                                        <div class="details-label">Who is the lead booker?</div>
                                        <div class="row mb-md-5 mb-4">
                                            <div v-for="guest in guests" class="col-xl-12">
                                                <label class="guest-selector mb-1">
                                                    <input type="radio"
                                                           v-on:click="guestSelected = true"
                                                           v-model="selectedGuest"
                                                           v-bind:value="guest" />
                                                    {{ guest.firstName }} {{ guest.lastName }}
                                                </label>
                                            </div>
                                            <div class="col-xl-12">
                                                <label class="guest-selector mb-1">
                                                    <input type="radio"
                                                           v-model="selectedGuest"
                                                           v-bind:value="defaultGuest" />
                                                    Someone else
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <h4 class="mb-4 details-heading">Your details</h4>
                                </div>

                                <div class="form-group row">
                                    <div class="col-24 col-lg-12 col-xl-8 mb-4">
                                        <div class="details-label">Title</div>
                                        <Field name="title"
                                               as="select"
                                               class="form-select"
                                               v-if="!hasSelectedGuest"
                                               v-model="selectedGuest.title">
                                            <option v-for="title in titles"
                                                    v-bind:key="title.OperaCode"
                                                    v-bind:value="title.OperaCode">
                                                {{ title.DisplayName }}
                                            </option>
                                        </Field>

                                        <Field name="title"
                                               type="text"
                                               class="form-control"
                                               v-if="hasSelectedGuest"
                                               readonly
                                               v-model="selectedGuest.title"></Field>

                                        <ErrorMessage name="title" class="error-message" />
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <div class="col-xl-12 mb-4">
                                        <div class="details-label">First Name</div>

                                        <Field name="firstName"
                                               v-bind:readonly="hasSelectedGuest"
                                               class="form-control"
                                               v-model="selectedGuest.firstName"></Field>
                                        <ErrorMessage name="firstName" class="error-message" />
                                    </div>
                                    <div class="col-xl-12 mb-4">
                                        <div class="details-label">Last Name</div>
                                        <Field name="lastName"
                                               v-bind:readonly="hasSelectedGuest"
                                               class="form-control"
                                               v-model="selectedGuest.lastName"></Field>
                                        <ErrorMessage name="lastName" class="error-message" />
                                    </div>
                                </div>

                                <div class="form-group row mb-4">
                                    <div class="col-24">
                                        <div class="details-label">Email</div>
                                        <Field name="email"
                                               type="email"
                                               class="form-control"
                                               v-model="selectedGuest.email"></Field>
                                        <ErrorMessage name="email" class="error-message" />
                                    </div>
                                </div>

                                <div class="form-group row mb-4">
                                    <div class="col-24">
                                        <div class="details-label">Telephone</div>
                                        <TelephoneInput v-model="selectedGuest.telephone"></TelephoneInput>
                                        <ErrorMessage name="telephone" class="error-message" />
                                    </div>
                                </div>

                                <div class="form-group row mb-4">
                                    <div class="col-24">
                                        <div class="details-label">Address</div>

                                        <Field name="address.country"
                                               as="select"
                                               class="form-select"
                                               v-model="selectedCountry">
                                            <option v-for="country in countries"
                                                    v-bind:key="country.OperaCode"
                                                    v-bind:value="country.OperaCode">
                                                {{ country.DisplayName }}
                                            </option>
                                        </Field>
                                        <ErrorMessage name="address.country"
                                                      class="error-message" />
                                    </div>
                                </div>
                                <Field name="address.isManualEntry"
                                       type="hidden"
                                       v-model="manualAddressEntry"></Field>

                                <div v-if="selectedCountry == 'GB' && !manualAddressEntry">
                                    <div class="form-group row mb-4 flex-row g-0">
                                        <div class="col-xl-12">
                                            <input type="text"
                                                   class="form-control"
                                                   placeholder="Enter postcode"
                                                   v-model="searchPostcode" />
                                        </div>
                                        <div class="col-xl-12 mt-xl-0 mt-lg-3 mt-2">
                                            <button class="btn btn--gold h-100"
                                                    type="button"
                                                    v-on:click="onLookupAddress">
                                                Find Address
                                            </button>
                                        </div>
                                    </div>

                                    <div v-if="addressList">
                                        <div class="form-group row mb-4">
                                            <div class="col">
                                                <select class="form-select"
                                                        v-on:change="onSelectAddress"
                                                        v-model="selectedAddress">
                                                    <option disabled selected v-bind:value="null">
                                                        Please select...
                                                    </option>
                                                    <option v-bind:value="address"
                                                            v-for="address in addressList">
                                                        {{ address.line1 }} {{ address.locality }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="form-group row"
                                         v-if="!manualAddressEntry && selectedAddress == null">
                                        <div class="col-24">
                                            <button type="button"
                                                    class="btn btn--manual-address"
                                                    v-on:click="manualAddressEntry = true">
                                                Manually Enter Address
                                            </button>
                                        </div>
                                    </div>
                                    <div class="mb-4">
                                        <Field name="address.selectedAddress"
                                               type="hidden"
                                               v-model="localAddress.postCode"></Field>

                                        <ErrorMessage name="address.selectedAddress"
                                                      class="error-message" />
                                    </div>
                                </div>

                                <div v-show="
                    manualAddressEntry ||
                    selectedCountry != 'GB' ||
                    selectedAddress != null
                  ">
                                    <div class="form-group row mb-4">
                                        <div class="col-24">
                                            <div class="details-label">First line of address</div>

                                            <Field name="address.line1"
                                                   class="form-control"
                                                   v-model="localAddress.line1"></Field>
                                            <ErrorMessage name="address.line1"
                                                          class="error-message" />
                                        </div>
                                    </div>

                                    <div class="form-group row mb-4">
                                        <div class="col-24">
                                            <div class="details-label">
                                                Second line of address (optional)
                                            </div>

                                            <Field name="address.line2"
                                                   class="form-control"
                                                   v-model="localAddress.line2"></Field>
                                            <ErrorMessage name="address.line2"
                                                          class="error-message" />
                                        </div>
                                    </div>

                                    <div class="form-group row mb-4">
                                        <div class="col-xl-18">
                                            <div class="details-label">City</div>
                                            <Field name="address.city"
                                                   class="form-control"
                                                   v-model="localAddress.city"></Field>
                                            <ErrorMessage name="address.city" class="error-message" />
                                        </div>
                                    </div>

                                    <div class="form-group row mb-4">
                                        <div class="col-xl-12">
                                            <div class="details-label">Postcode</div>
                                            <Field name="address.postCode"
                                                   class="form-control"
                                                   v-model="localAddress.postCode"></Field>
                                            <ErrorMessage name="address.postCode"
                                                          class="error-message" />
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group row mb-4">
                                    <div class="col-24">
                                        <h3 class="mb-3">Additional information</h3>
                                        <Field name="specialRequests"
                                               class="form-control"
                                               as="textarea"
                                               rows="5"
                                               placeholder="To ensure we are able to create the perfect experience for you, please provide us with details of any current medical treatment you are undergoing; along with any medical issues or allergies."></Field>
                                        <ErrorMessage name="specialRequests"
                                                      class="error-message" />
                                    </div>
                                </div>

                                <div class="row form-group mb-2">
                                    <div class="col-24">
                                        <label class="terms-label d-flex align-items-center">
                                            <Field name="medical"
                                                   type="checkbox"
                                                   value="true"></Field>
                                            <span class="terms-label--copy">
                                                I can confirm I have detailed above any medical issues,
                                                medical treatments and/or allergies.
                                            </span>
                                        </label>
                                        <ErrorMessage name="medical" class="error-message" />
                                    </div>
                                </div>

                                <div class="row form-group">
                                    <div class="col-24">
                                        <label class="terms-label d-flex align-items-center">
                                            <Field name="terms" type="checkbox" value="true"></Field>
                                            <span class="terms-label--copy">
                                                I agree to the
                                                <a href="/privacy-cookie-policy/" target="_blank">privacy notice</a>
                                                and
                                                <a href="/general-booking-terms/" target="_blank">terms of use</a>
                                            </span>
                                        </label>
                                        <ErrorMessage name="terms" class="error-message" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-slate-blue p-5 account-panel"
                             v-if="!isAuthenticated">
                            <div v-if="!isSomeoneElseBooking">
                                <h4 class="details-heading mb-4">
                                    Create an account for all these benefits:
                                </h4>
                                <ul>
                                    <li>View all your bookings in one place</li>
                                    <li>Easily amend your booking</li>
                                    <li>Upgrade your stay</li>
                                </ul>

                                <p>
                                    Just enter a password and we'll create an account for you
                                    (optional)
                                </p>

                                <div class="row form-group mb-2">
                                    <div class="col-18">
                                        <Field autocomplete="new-password"
                                               name="account.password1"
                                               type="password"
                                               class="form-control"
                                               placeholder="Choose a password"></Field>
                                        <ErrorMessage name="account.password1"
                                                      class="error-message" />
                                    </div>
                                </div>

                                <div class="row form-group mb-4">
                                    <div class="col-18">
                                        <Field autocomplete="new-password"
                                               name="account.password2"
                                               type="password"
                                               class="form-control"
                                               placeholder="Re-enter password"></Field>
                                        <ErrorMessage name="account.password2"
                                                      class="error-message" />
                                    </div>
                                </div>
                            </div>

                            <div class="row form-group">
                                <div class="col-24">
                                    <label class="terms-label d-flex align-items-center">
                                        <input autocomplete="new-password"
                                               type="checkbox"
                                               v-on:click="toggleMarketing()" />

                                        <span class="terms-label--copy">Join the mailing list (optional)</span>
                                    </label>
                                </div>
                            </div>

                            <div class="row form-group mt-3 mb-3" v-if="showMarketingBoxes">
                                <div class="col-xl-8">
                                    <label class="terms-label">
                                        <Field name="account.addressBooks"
                                               type="checkbox"
                                               value="3507983"></Field>
                                        Spa
                                    </label>
                                </div>

                                <div class="col-xl-8">
                                    <label class="terms-label">
                                        <Field name="account.addressBooks"
                                               type="checkbox"
                                               value="3508013"></Field>
                                        Golf
                                    </label>
                                </div>

                                <div class="col-xl-8">
                                    <label class="terms-label">
                                        <Field name="account.addressBooks"
                                               type="checkbox"
                                               value="3508001"></Field>
                                        Dining
                                    </label>
                                </div>

                                <div class="col-xl-8">
                                    <label class="terms-label">
                                        <Field name="account.addressBooks"
                                               type="checkbox"
                                               value="3508068"></Field>
                                        Events
                                    </label>
                                </div>

                                <div class="col-xl-8">
                                    <label class="terms-label">
                                        <Field name="account.addressBooks"
                                               type="checkbox"
                                               value="3507997"></Field>
                                        Rooms
                                    </label>
                                </div>

                                <div class="col-xl-8">
                                    <label class="terms-label">
                                        <Field name="addressBooks"
                                               type="checkbox"
                                               value="21980355"></Field>
                                        Offers
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <actions-bar>
            <template v-slot:actions>
                <div class="row d-none d-sm-flex">
                    <div class="col-10">
                        <button v-on:click="onBack"
                                type="button"
                                class="btn btn--transparent btn--back">
                            Back
                        </button>
                    </div>
                    <div class="col-4 d-flex flex-column align-items-center">
                        <span v-if="total > 0" class="spa-total-label">Total</span>
                        <span v-if="total > 0" class="spa-total">&pound;{{ total }}</span>
                    </div>

                    <div class="col-10 d-flex justify-content-end">
                        <button class="btn btn--slate-blue btn--continue" type="submit">
                            Check your booking
                        </button>
                    </div>
                </div>

                <div class="row d-sm-none">
                    <div class="col-24 d-flex flex-row align-items-center justify-content-center mb-3">
                        <span v-if="total > 0" class="spa-total-label me-2">Total</span>
                        <span v-if="total > 0" class="spa-total">&pound;{{ total }}</span>
                    </div>

                    <div class="col-12">
                        <button v-on:click="onBack"
                                type="button"
                                class="btn btn--transparent btn--back">
                            Back
                        </button>
                    </div>

                    <div class="col-12 d-flex justify-content-end">
                        <button class="btn btn--slate-blue btn--continue" type="submit">
                            Check your booking
                        </button>
                    </div>
                </div>
            </template>
        </actions-bar>
    </Form>
</template>
<script>
    import { Field, Form, ErrorMessage } from "vee-validate";
    import * as yup from "yup";
    import "yup-phone";
    import ActionsBar from "@/shared/components/ActionsBar.vue";
    import { store } from "../store/index";
    import { accountStore } from "@/account/store/index";
    import addressapi from "@/shared/api/address";
    import TelephoneInput from "@/shared/components/TelephoneInput.vue";

    export default {
        name: "StayDetails",

        components: {
            ActionsBar,
            Field,
            Form,
            ErrorMessage,
            TelephoneInput,
        },
        mounted() {
            if (this.isAuthenticated && this.userProfile != null) {
                this.selectedGuest = {
                    title: this.userProfile.Title,
                    firstName: this.userProfile.FirstName,
                    lastName: this.userProfile.LastName,
                    email: this.userProfile.EmailAddress,
                    telephone: this.userProfile.Telephone,
                };

                if (
                    this.userProfile.Address != undefined &&
                    this.userProfile.Address != null
                ) {
                    this.localAddress = {
                        line1: this.userProfile.Address.Line1,
                        line2: this.userProfile.Address.Line2,
                        city: this.userProfile.Address.City,
                        country: this.userProfile.Address.Country,
                        postCode: this.userProfile.Address.PostCode,
                    };

                    this.manualAddressEntry = true;
                }
            }

            // temporarily enable manual address as address lookup is broken
            this.manualAddressEntry = true
        },
        data() {
            const schema = yup.object().shape({
                title: yup.string().required().label("Title"),
                firstName: yup.string().required().label("First name"),
                lastName: yup.string().required().label("Last name"),
                email: yup.string().required().email().label("Email address"),
                telephone: yup.string().required().label("Telephone"),
                address: yup.object().shape({
                    line1: yup.string().required().label("Address line 1"),
                    line2: yup.string().nullable().notRequired(),
                    city: yup.string().required().label("City"),
                    country: yup.string().required().label("Country"),
                    postCode: yup.string().required().label("Postcode"),
                }),
                medical: yup
                    .boolean()
                    .required()
                    .label(
                        "Medical issues, medical treatments, allergies and/or dietary requirements"
                    ),
                terms: yup
                    .boolean()
                    .required()
                    .label("Privacy notice and terms and conditions"),
                account: yup.object().shape({
                    password1: yup.string().notRequired().label("Password"),
                    password2: yup
                        .string()
                        .notRequired()
                        .test("passwords-match", "Passwords must match", function (value) {
                            return this.parent.password1 === value;
                        }),
                }),
                specialRequests: yup.string().notRequired(),
            });

            return {
                schema,
                selectedCountry: "GB",
                defaultGuest: { title: "", firstName: "", lastName: "" },
                activeRequestsIndex: 0,
                selectedGuest: {},
                guestSelected: false,
                manualAddressEntry: false,
                addressList: null,
                searchPostcode: null,
                selectedAddress: null,
                localAddress: {
                    line1: "",
                    line2: "",
                    city: "",
                    country: "",
                    postCode: "",
                },
                error: false,
                errorText: "",
                isLoading: false,
                showMarketingBoxes: false,

                //acceptedTerms: false,
            };
        },

        computed: {
            userProfile() {
                return accountStore.getters.userProfile;
            },
            hasSelectedGuest() {
                return this.guestSelected && this.selectedGuest != this.defaultGuest;
            },
            isAuthenticated() {
                return accountStore.getters.isAuthenticated;
            },
            session() {
                return store.getters.session;
            },
            booking() {
                return store.getters.booking;
            },
            titles() {
                return store.getters.configuration.Titles;
            },
            countries() {
                return store.getters.configuration.Countries;
            },
            content() {
                return store.getters.configuration.Content;
            },
            isSomeoneElseBooking() {
                return this.defaultGuest == this.selectedGuest;
            },
            search() {
                return store.getters.search;
            },
            total() {
                return store.getters.total;
            },
            guests() {
                var search = store.getters.search;

                var result = [];
                for (var gi in search.guests) {
                    var guest = search.guests[gi];

                    result.push({
                        guestIndex: gi,
                        title: guest.title,
                        firstName: guest.firstName,
                        lastName: guest.lastName,
                        isLoggedInUser: guest.isLoggedInUser,
                    });
                }

                return result;
            },
        },
        methods: {
            toggleMarketing() {
                this.showMarketingBoxes = !this.showMarketingBoxes;
            },
            getLegNames: function (leg, hasAnd) {
                var booking = store.getters.booking;
                var leg = booking.legs.find((item) => item.leg == leg);
                var names = "";
                for (var i = 0; i < leg.guests.length; i++) {
                    let guest = leg.guests[i];
                    names += guest.firstName;

                    if (leg.guests.length - 2 == i) {
                        names += hasAnd ? " & " : " or ";
                    } else if (leg.guests.length > i + 1) {
                        names += ", ";
                    }
                }
                return names.trim();
            },
            onSelectAddress: function () {
                if (this.selectedAddress != null) {
                    this.localAddress.line1 = this.selectedAddress.line1;
                    this.localAddress.line2 = this.selectedAddress.locality;
                    this.localAddress.city = this.selectedAddress.city;
                    this.localAddress.postCode = this.searchPostcode.toUpperCase();
                }
                this.addressList = null;
            },
            onLookupAddress: function () {
                var payload = {
                    postalCode: this.searchPostcode,
                };

                addressapi.lookupAddress(payload, (t) => {
                    this.addressList = t.addresses;
                });
            },

            onBack: function () {
                this.$router.push({
                    name: "stay_addons",
                    params: { id: this.session.Id },
                });
            },

            onSubmit: function (values) {
                this.trackBeginCheckout();
                let o = this;
                this.isLoading = true;
                let thisSelectedGuest = null;

                if (!this.isAuthenticated) {
                    if (this.selectedGuest != this.defaultGuest) {
                        thisSelectedGuest = {
                            guestIndex: this.selectedGuest.guestIndex,
                        };
                    }
                } else {
                    thisSelectedGuest = {
                        guestIndex:
                            this.guests.find((item) => item.isLoggedInUser)?.guestIndex || 0,
                    };
                }

                var payload = {
                    selectedGuest: thisSelectedGuest,
                    isSpaDay: false,
                    leadGuest: {
                        title: values.title,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        emailAddress: values.email,
                        telephone: values.telephone,
                        address: values.address,
                    },
                    requests: values.specialRequests,
                    account: values.account,
                };

                var errorBox = document.getElementById("error-box");
                this.isLoading = true;

                store
                    .dispatch("validateFields", payload)
                    .then(() => {
                        this.isLoading = false;
                        store.dispatch("attachDetails", payload).then(() => {
                            if (
                                payload.account != undefined &&
                                payload.account.password1 !== undefined &&
                                payload.account.password1.length
                            ) {
                                var accountRegistrationPayload = {
                                    title: payload.leadGuest.title,
                                    firstName: payload.leadGuest.firstName,
                                    lastName: payload.leadGuest.lastName,
                                    emailAddress: payload.leadGuest.emailAddress,
                                    telephone: payload.leadGuest.telephone,
                                    address: payload.leadGuest.address,
                                    password: payload.account.password1,
                                    addressBooks: this.showMarketingBoxes
                                        ? values.account.addressBooks
                                        : null,
                                };

                                accountStore
                                    .dispatch("register", accountRegistrationPayload)
                                    .then((t) => {
                                        var loginPayload = {
                                            username: accountRegistrationPayload.emailAddress,
                                            password: accountRegistrationPayload.password,
                                        };
                                        accountStore.dispatch("login", loginPayload).then(() => {
                                            accountStore.dispatch("loadSession").then(() => {
                                                store.dispatch("setAccountStatus", {
                                                    attempted: true,
                                                    failed: false,
                                                });
                                                store.dispatch("beginBooking", o.session.Id).then(() => {
                                                    this.$router.push({
                                                        name: "spa_treatment_summary",
                                                        params: { id: this.session.Id },
                                                    });
                                                });
                                            });
                                        });
                                    })
                                    .catch((t) => {
                                        store.dispatch("setAccountStatus", {
                                            attempted: true,
                                            failed: true,
                                        });
                                        this.$router.push({
                                            name: "spa_treatment_summary",
                                            params: { id: this.session.Id },
                                        });
                                    });
                            } else {
                                store.dispatch("beginBooking", o.session.Id).then(() => {
                                    this.$router.push({
                                        name: "spa_treatment_summary",
                                        params: { id: this.session.Id },
                                    });
                                });
                            }
                        });
                    })
                    .catch((data) => {
                        this.loading = false;
                        this.errorText = data.error;
                        this.error = true;
                        window.scrollTo(errorBox.getBoundingClientRect().top, 0);
                    });
            },
            getTotalGuests(leg) {
                return leg.guests.length;
            },
            getTotalDogs(leg) {
                return leg.guestQuota.Dogs;
            },

            trackBeginCheckout() {
                if (this.$gtm.enabled()) {
                    let items = [];
                    let total = 0;

                    if (this.search.selectedMudExtras?.selection != null) {
                        items.push({
                            item_id: this.search.selectedMudExtras?.code.Code,
                            item_name: this.search.selectedMudExtras?.addOn.content.DisplayName,
                            item_variant:
                                this.search.selectedMudExtras?.code.Guests + " Guests",
                            quantity: 1,
                            price: +this.search.selectedMudExtras.selection.ActualPrice,
                            item_category: "Spa Add Ons",
                        });

                        total += +this.search.selectedMudExtras.selection.ActualPrice;
                    }

                    for (var gi = 0; gi < this.search.guests.length; gi++) {
                        let guest = this.search.guests[gi];
                        let treatments = guest.treatments;
                        for (let ti = 0; ti < treatments.length; ti++) {
                            let treatment = treatments[ti];

                            items.push({
                                item_id: treatment.duration.Code,
                                item_name: treatment.treatment.DisplayName,
                                quantity: 1,
                                price: +treatment.duration.Price,
                                item_category: "Treatments",
                            });

                            total += +treatment.duration.Price;
                        }
                    }

                    window.dataLayer?.push({
                        pageURL: "/spa/treatment/details",
                        event: "begin_checkout",
                        value: total,
                        curency: "GBP",
                        ecommerce: {
                            items: items,
                        },
                    });
                }
            },
        },
    };
</script>

<style src="@/shared/css/details.css"></style>
<style scoped>
    .info-box {
        display: block;
    }

    .spa-total-label {
        font-weight: 300;
        display: block;
        font-size: 16px;
        text-align: center;
    }

    .spa-total {
        font-weight: bold;
        display: block;
        font-size: 24px;
        text-align: center;
    }

    .terms-label--copy {
        line-height: 1.2;
        position: relative;
        top: 6px;
    }

    input[type="checkbox"] {
        min-width: 28px;
        min-height: 28px;
    }
</style>
